import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import './Charts.css'

/**
 * DonutChart Component
 *
 * A donut chart (pie chart with center cutout) with center label support.
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array
 * @param {string} props.labelKey - Key for labels in data
 * @param {string} props.valueKey - Key for values in data
 * @param {string} props.title - Chart title
 * @param {boolean} props.showLegend - Show legend
 * @param {string|number} props.centerLabel - Label to show in center
 * @param {string} props.centerSublabel - Sublabel in center
 * @param {Array} props.colors - Custom color palette
 * @param {Function} props.formatValue - Value formatter function
 * @param {number} props.size - Chart size in pixels
 * @param {number} props.thickness - Donut thickness (0-1)
 */
const DonutChart = ({
	data = [],
	labelKey = 'label',
	valueKey = 'value',
	title,
	showLegend = true,
	centerLabel,
	centerSublabel,
	colors = defaultColors,
	formatValue = (v) => v,
	size = 280,
	thickness = 0.35,
}) => {
	const [hoveredIndex, setHoveredIndex] = useState(null)

	// Calculate dimensions
	const outerRadius = size / 2 - 10
	const innerRadius = outerRadius * (1 - thickness)
	const centerX = size / 2
	const centerY = size / 2

	// Calculate total
	const total = useMemo(() => {
		return data.reduce((sum, item) => sum + (item[valueKey] || 0), 0)
	}, [data, valueKey])

	// Calculate segments
	const segments = useMemo(() => {
		if (!total) return []

		let currentAngle = -90 // Start from top
		return data.map((item, index) => {
			const value = item[valueKey] || 0
			const percentage = total ? (value / total) * 100 : 0
			const angle = (percentage / 100) * 360

			const segment = {
				...item,
				value,
				percentage,
				startAngle: currentAngle,
				endAngle: currentAngle + angle,
				color: item.color || colors[index % colors.length],
			}

			currentAngle += angle
			return segment
		})
	}, [data, valueKey, total, colors])

	// Generate arc path
	const getArcPath = (segment, isHovered) => {
		const outer = isHovered ? outerRadius * 1.05 : outerRadius
		const inner = innerRadius

		const startRad = (segment.startAngle * Math.PI) / 180
		const endRad = (segment.endAngle * Math.PI) / 180

		const x1Outer = centerX + outer * Math.cos(startRad)
		const y1Outer = centerY + outer * Math.sin(startRad)
		const x2Outer = centerX + outer * Math.cos(endRad)
		const y2Outer = centerY + outer * Math.sin(endRad)

		const x1Inner = centerX + inner * Math.cos(startRad)
		const y1Inner = centerY + inner * Math.sin(startRad)
		const x2Inner = centerX + inner * Math.cos(endRad)
		const y2Inner = centerY + inner * Math.sin(endRad)

		const largeArc = segment.endAngle - segment.startAngle > 180 ? 1 : 0

		// For full circle
		if (segment.percentage >= 99.9) {
			return `
				M ${centerX + outer},${centerY}
				A ${outer},${outer} 0 1 1 ${centerX - outer},${centerY}
				A ${outer},${outer} 0 1 1 ${centerX + outer},${centerY}
				M ${centerX + inner},${centerY}
				A ${inner},${inner} 0 1 0 ${centerX - inner},${centerY}
				A ${inner},${inner} 0 1 0 ${centerX + inner},${centerY}
				Z
			`
		}

		return `
			M ${x1Outer},${y1Outer}
			A ${outer},${outer} 0 ${largeArc} 1 ${x2Outer},${y2Outer}
			L ${x2Inner},${y2Inner}
			A ${inner},${inner} 0 ${largeArc} 0 ${x1Inner},${y1Inner}
			Z
		`
	}

	// Default center content
	const getCenterLabel = () => {
		if (centerLabel !== undefined) return centerLabel
		if (hoveredIndex !== null && segments[hoveredIndex]) {
			return `${segments[hoveredIndex].percentage.toFixed(0)}%`
		}
		return formatValue(total)
	}

	const getCenterSublabel = () => {
		if (hoveredIndex !== null && segments[hoveredIndex]) {
			return segments[hoveredIndex][labelKey]
		}
		return centerSublabel || 'Total'
	}

	if (!data.length || !total) {
		return (
			<div className="chart chart--empty">
				<p>No data available</p>
			</div>
		)
	}

	return (
		<div className="chart donut-chart">
			{title && <h4 className="chart__title">{title}</h4>}

			<div className="donut-chart__container">
				<div className="donut-chart__chart-wrapper">
					<svg
						width={size}
						height={size}
						viewBox={`0 0 ${size} ${size}`}
						className="donut-chart__svg"
					>
						{segments.map((segment, index) => (
							<path
								key={index}
								d={getArcPath(segment, hoveredIndex === index)}
								fill={segment.color}
								stroke="white"
								strokeWidth="2"
								className={`donut-chart__segment ${hoveredIndex === index ? 'donut-chart__segment--hovered' : ''}`}
								onMouseEnter={() => setHoveredIndex(index)}
								onMouseLeave={() => setHoveredIndex(null)}
							>
								<title>
									{segment[labelKey]}: {formatValue(segment.value)} ({segment.percentage.toFixed(1)}%)
								</title>
							</path>
						))}
					</svg>

					{/* Center Content */}
					<div className="donut-chart__center">
						<span className="donut-chart__center-label">{getCenterLabel()}</span>
						<span className="donut-chart__center-sublabel">{getCenterSublabel()}</span>
					</div>
				</div>

				{showLegend && (
					<div className="donut-chart__legend">
						{segments.map((segment, index) => (
							<div
								key={index}
								className={`donut-chart__legend-item ${hoveredIndex === index ? 'donut-chart__legend-item--active' : ''}`}
								onMouseEnter={() => setHoveredIndex(index)}
								onMouseLeave={() => setHoveredIndex(null)}
							>
								<span
									className="donut-chart__legend-color"
									style={{ backgroundColor: segment.color }}
								/>
								<span className="donut-chart__legend-label">{segment[labelKey]}</span>
								<span className="donut-chart__legend-value">
									{formatValue(segment.value)}
								</span>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}

// Default colors
const defaultColors = [
	'#3b82f6', // blue
	'#10b981', // green
	'#f59e0b', // amber
	'#ef4444', // red
	'#8b5cf6', // violet
	'#ec4899', // pink
	'#06b6d4', // cyan
	'#84cc16', // lime
]

DonutChart.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	labelKey: PropTypes.string,
	valueKey: PropTypes.string,
	title: PropTypes.string,
	showLegend: PropTypes.bool,
	centerLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	centerSublabel: PropTypes.string,
	colors: PropTypes.arrayOf(PropTypes.string),
	formatValue: PropTypes.func,
	size: PropTypes.number,
	thickness: PropTypes.number,
}

export default DonutChart
